import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Snackbar,
  CssBaseline,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../apiBase';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setToastSeverity('error');
      setToastMessage('Please enter your email address');
      setToastOpen(true);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();
      
      if (res.ok) {
        setEmailSent(true);
        setToastSeverity('success');
        setToastMessage(data.message || 'Password reset link has been sent to your email');
        setToastOpen(true);
      } else {
        setToastSeverity('error');
        setToastMessage(data.error || 'Failed to send reset link');
        setToastOpen(true);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setToastSeverity('error');
      setToastMessage('An error occurred. Please try again later.');
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CssBaseline />

      {/* Main Card */}
      <Paper
        elevation={4}
        sx={{
          zIndex: 2,
          p: 5,
          width: '100%',
          maxWidth: '450px',
          borderRadius: '24px',
          textAlign: 'center',
          bgcolor: 'white',
        }}
      >
        {!emailSent ? (
          <>
            <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: '#2c3e50' }}>
              Forgot Password?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#f5f6fa',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: '#42a5f5',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#2196f3',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                  },
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textDecoration: 'none',
                    color: '#42a5f5',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  Back to Login
                </Link>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#e3f2fd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Typography variant="h3" sx={{ color: '#42a5f5' }}>
                  ✓
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="700" gutterBottom sx={{ color: '#2c3e50' }}>
                Check Your Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                We've sent a password reset link to:
              </Typography>
              <Typography variant="body1" fontWeight="600" sx={{ mt: 1, color: '#42a5f5' }}>
                {email}
              </Typography>
              <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Didn't receive the email?</strong>
                  <br />
                  • Check your spam/junk folder
                  <br />
                  • Make sure you entered the correct email address
                  <br />
                  • The link expires in 1 hour
                </Typography>
              </Alert>
            </Box>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: '#42a5f5',
                color: '#42a5f5',
                '&:hover': {
                  borderColor: '#2196f3',
                  bgcolor: '#e3f2fd',
                },
              }}
            >
              Back to Login
            </Button>
          </>
        )}
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
